import React, { useEffect, useRef, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { TeamAccess } from "target_wasm";
import { TooltipButton } from "components/common/TooltipButton";
import TeamLabel from "components/common/TeamLabel";
import { navigatorButtonClasses } from "components/common/navigatorStyles";

interface TeamNavigatorProps {
  teamName: string;
  currentTeamId: string;
  teams: TeamAccess[];
  onNavigateToTeams: () => void;
  onSelectTeam: (targetTeamId: string) => void;
  onShowTeamDetails: () => void;
}

export const TeamNavigator: React.FC<TeamNavigatorProps> = ({
  teamName,
  currentTeamId,
  teams,
  onNavigateToTeams,
  onSelectTeam,
  onShowTeamDetails,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const otherTeams = teams.filter(
    (team) => team.team_id_str() !== currentTeamId,
  );
  const hasAlternateTeams = otherTeams.length > 0;

  useEffect(() => {
    if (!isDropdownOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleArrowClick = () => {
    if (hasAlternateTeams) {
      setIsDropdownOpen((prev) => !prev);
    }
  };

  const handleTeamSelection = (targetTeamId: string) => {
    setIsDropdownOpen(false);
    onSelectTeam(targetTeamId);
  };

  return (
    <div className="flex items-center gap-2 item-height">
      <button
        type="button"
        onClick={onNavigateToTeams}
        className={`${navigatorButtonClasses} text-fg-muted-semibold cursor-pointer`}
      >
        Teams
      </button>
      <span className="text-fg-muted-regular flex items-center">/</span>
      <div
        className="flex items-center gap-1 relative cursor-pointer"
        ref={dropdownRef}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={onShowTeamDetails}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onShowTeamDetails();
            }
          }}
          className={`${navigatorButtonClasses} gap-1 text-fg-semibold rounded-app `}
        >
          <span className="truncate">{teamName || "Untitled Team"}</span>
          {hasAlternateTeams && (
            <TooltipButton
              variant="contextual"
              size="xs"
              onClick={(event) => {
                event.stopPropagation();
                handleArrowClick();
              }}
              title="Change Team"
            >
              <KeyboardArrowDownIcon fontSize="inherit" />
            </TooltipButton>
          )}
        </div>
        {isDropdownOpen && hasAlternateTeams && (
          <div
            className="sidebar-dropdown dropdown-from-top max-h-60 overflow-y-auto"
            style={{
              position: "absolute",
              left: 0,
              top: "100%",
              marginTop: "0.25rem",
            }}
          >
            {otherTeams.map((team) => (
              <button
                key={team.team_id_str()}
                type="button"
                onClick={() => handleTeamSelection(team.team_id_str())}
                className="menu-item flex items-center gap-2"
              >
                <TeamLabel team={team} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
